import { Box, Button, Stack, TableCell, TableRow, styled } from '@mui/material';
import { DeleteIcon } from '@synergy/theme-next';
import { FormattedMessage } from 'react-intl';
import {
    PoolBanner,
    PoolImage,
    PoolLink,
    PoolQuantityPicker,
    PoolQuickActionButton,
    PoolRow,
} from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement, useState } from 'react';

import { ArticlePriceMobile } from './article-price-mobile';
import { Attribute } from '../../../../models/attribute';
import { DeliveryStatus } from '../../../../components/delivery-status/delivery-status';
import { FavoriteIconButton } from '../../../../components/favorite-button/favorite-icon-button';
import { ROUTES } from '../../../../utils/routes';
import { ShoppingCartItem, ShoppingCartItemStatus } from '../../../../models/shopping-cart';
import { VerticalDivider } from '../../../../components/vertical-divider/vertical-divider';
import { addToWishlist } from '../../../../store/whish-list-actions';
import { getBannerMessage } from '../utils/get-banner-message';
import { openInquiryModal } from '../../../../store/single-inquiry-actions';
import { removeArticleFromCart, setArticleQuantity } from '../../../../store/shopping-cart-article-actions';
import { useAppDispatch } from '../../../../../store/store';

const ProductImage = styled(PoolImage)(() => ({
    display: 'block',
    objectFit: 'contain',
    height: 100,
    width: 100,
}));

export interface SupplierTableRowProps {
    cartItem: ShoppingCartItem;
    previewImage: string | undefined;
}

export const RowArticleMobile = (props: SupplierTableRowProps): ReactElement => {
    const dispatch = useAppDispatch();
    const { cartItem, previewImage } = props;
    const article = cartItem.product;

    const [isFavorite, setIsFavorite] = useState(false);
    const [quantity, setQuantity] = useState(cartItem.quantity);

    const handleAddToWishlist = (): void => {
        if (!isFavorite) {
            setIsFavorite(true);
            dispatch(addToWishlist('default', article.id, article.minimumOrderQuantity));
        }
    };

    const handleDeleteArticle = (): void => {
        dispatch(removeArticleFromCart(cartItem.id));
    };

    const handleQuantityChange = (quantity: number): void => {
        dispatch(setArticleQuantity(cartItem.id, quantity));
        setQuantity(quantity);
    };

    const handleCreateSingleInquiry = (): void => {
        dispatch(
            openInquiryModal({
                items: [{ id: article.id, articleName: article.name, quantity: article.minimumOrderQuantity }],
            })
        );
    };

    return (
        <>
            {cartItem.status !== ShoppingCartItemStatus.Available && (
                <TableRow>
                    <TableCell sx={{ px: 0, pb: 0, pt: 2, borderBottom: 'none' }}>
                        <PoolBanner severity={cartItem.status === ShoppingCartItemStatus.Changed ? 'warning' : 'error'}>
                            <FormattedMessage id={getBannerMessage(cartItem.status)} />
                        </PoolBanner>
                    </TableCell>
                </TableRow>
            )}

            <TableRow>
                <TableCell sx={{ position: 'relative' }}>
                    {cartItem.status !== ShoppingCartItemStatus.Removed && (
                        <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
                            <FavoriteIconButton onClick={handleAddToWishlist} isFavorite={isFavorite} />
                        </Box>
                    )}
                    <PoolRow alignItems="flex-start" gap={2} sx={{ py: 1 }}>
                        <Stack justifyContent="flex-start">
                            <PoolLink href={ROUTES.article.detail.replace(':id', article.id.toString())}>
                                <ProductImage
                                    src={previewImage}
                                    alt={article.name}
                                    sx={{
                                        opacity: cartItem.status !== ShoppingCartItemStatus.Removed ? 1 : 0.5,
                                    }}
                                />
                            </PoolLink>
                        </Stack>

                        <PoolRow gap={0} alignItems="top" sx={{ width: '100%' }}>
                            <Stack gap={1.5} typography="body2" flex={1}>
                                <Stack
                                    sx={{ opacity: cartItem.status !== ShoppingCartItemStatus.Removed ? 1 : 0.5 }}
                                    color="grey.800"
                                >
                                    <PoolLink
                                        colorVariant="black"
                                        href={ROUTES.article.detail.replace(':id', article.id.toString())}
                                        typography="subtitle1"
                                    >
                                        {article.name}
                                    </PoolLink>
                                    <Box sx={{ mt: 1 }}>
                                        <PoolFormattedMessage
                                            id={'app.cart.article.articleNumber'}
                                            values={{ articleNumber: article.number }}
                                        />
                                    </Box>
                                    {article.globalTradeItemNumber && (
                                        <Box>
                                            <PoolFormattedMessage
                                                id={'app.cart.article.globalNumber'}
                                                values={{ articleNumber: article.globalTradeItemNumber }}
                                            />
                                        </Box>
                                    )}

                                    {article.attributes && (
                                        <PoolRow divider={<VerticalDivider />} flexWrap="wrap" rowGap={0}>
                                            {article.attributes.map((attribute: Attribute) => (
                                                <Box key={attribute.id}>
                                                    {attribute.name} {attribute.value}
                                                </Box>
                                            ))}
                                        </PoolRow>
                                    )}
                                </Stack>

                                {cartItem.status !== ShoppingCartItemStatus.Removed &&
                                    cartItem.status !== ShoppingCartItemStatus.PriceByRequest && (
                                        <Stack alignItems="flex-start">
                                            <ArticlePriceMobile
                                                article={article}
                                                netPrice={cartItem.netPrice}
                                                cartItem={cartItem}
                                                quantity={quantity}
                                            />
                                        </Stack>
                                    )}

                                <Stack gap={0.5}>
                                    <DeliveryStatus
                                        variant="circle"
                                        deliveryStatus={article.deliveryStatus}
                                        stockStatus={article.stockStatus}
                                        deliveryTimeInDays={article.deliveryTimeInDays}
                                    />
                                    {cartItem.status !== ShoppingCartItemStatus.Removed &&
                                        cartItem.status !== ShoppingCartItemStatus.PriceByRequest && (
                                            <>
                                                <PoolQuantityPicker
                                                    quantity={cartItem.quantity}
                                                    minQuantity={article.minimumOrderQuantity}
                                                    maxQuantity={article.maximumOrderQuantity}
                                                    interval={article.quantityInterval}
                                                    onQuantityChange={handleQuantityChange}
                                                />
                                                <Box color="grey.800" typography="body2">
                                                    <FormattedMessage
                                                        id="app.cart.article.quantity"
                                                        values={{
                                                            orderUnit: article.orderUnit,
                                                            contentQuantity: article.contentQuantity,
                                                            contentUnit: article.contentUnit,
                                                            optional: chunks =>
                                                                article.contentQuantity &&
                                                                article.contentUnit &&
                                                                chunks,
                                                        }}
                                                    />

                                                    {(article.minimumOrderQuantity ||
                                                        (article.quantityInterval &&
                                                            article.quantityInterval !== 1)) && (
                                                        <Stack direction="row">
                                                            {'('}
                                                            <Stack
                                                                direction="row"
                                                                typography="body2"
                                                                color="grey.800"
                                                                gap={1}
                                                                divider={<VerticalDivider />}
                                                            >
                                                                {article.minimumOrderQuantity && (
                                                                    <Box>
                                                                        <PoolFormattedMessage
                                                                            id={'app.cart.article.minimumQuantity'}
                                                                            values={{
                                                                                minimumQuantity:
                                                                                    article.minimumOrderQuantity,
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                {article.quantityInterval &&
                                                                    article.quantityInterval !== 1 && (
                                                                        <PoolFormattedMessage
                                                                            id={'app.cart.article.interval'}
                                                                            values={{
                                                                                interval: article.quantityInterval,
                                                                            }}
                                                                        />
                                                                    )}
                                                            </Stack>
                                                            {')'}
                                                        </Stack>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                </Stack>
                            </Stack>
                            <Box>
                                <PoolQuickActionButton
                                    onClick={handleDeleteArticle}
                                    color={cartItem.status !== ShoppingCartItemStatus.Changed ? 'error' : 'medium'} //medium
                                >
                                    <DeleteIcon fontSize="medium" />
                                </PoolQuickActionButton>
                            </Box>
                        </PoolRow>
                    </PoolRow>
                    {(cartItem.status === ShoppingCartItemStatus.Removed ||
                        cartItem.status === ShoppingCartItemStatus.PriceByRequest) && (
                        <Button variant="outlined" sx={{ my: 1 }} fullWidth={true} onClick={handleCreateSingleInquiry}>
                            <FormattedMessage id="app.singleInquiry" />
                        </Button>
                    )}
                </TableCell>
            </TableRow>
        </>
    );
};
